import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { IItemMask } from '@interfaces/item-mask';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IdentifierPipe } from '@shared/pipes-shared/pipes';
import { startWith, takeUntil } from 'rxjs/operators';
import { ReplaySubject, timer } from 'rxjs';
import { enumIdentifierQualifier } from '@data-layer/enums/common';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';

@Component({
  selector: 'app-identifier-hidden-control',
  templateUrl: './identifier-hidden-control.component.html',
  styleUrls: ['./identifier-hidden-control.component.scss'],
})
export class IdentifierHiddenControlComponent implements OnChanges, OnDestroy {
  @Input() public hide: boolean = false;
  @Input() public control: UntypedFormControl;
  @Input() public itemMask: IItemMask = {} as IItemMask;
  @Input() public errors: IErrorForm = null;
  @Input() public helper: string = null;
  @Input() public isDisabled: boolean = false;
  @Input() public entityIdentifierQualifier: enumIdentifierQualifier = enumIdentifierQualifier.EIN;
  @Input() public controlId: string = 'identifierControl';

  public hiddenControl: UntypedFormControl = new UntypedFormControl();
  public showHiddenControl: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private companiesService: CompaniesService, private identifierPipe: IdentifierPipe) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.control && this.control) {
      this.control.valueChanges.pipe(startWith(this.control.value), takeUntil(this.destroy$)).subscribe((value) => {
        if (this.hiddenControl.value !== this.identifierPipe.transform(value)) {
          this.hiddenControl.patchValue(this.identifierPipe.transform(value));
        }
      });
    }

    if (changes.hide && changes.hide.previousValue !== changes.hide.currentValue) {
      if (this.hide) {
        this.hideControl();
      } else {
        this.showControl();
      }
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public showControl(focus: boolean = false) {
    this.showHiddenControl = false;
    if (focus) {
      timer(0)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.identifierControl) {
            this.identifierControl.focus();
          }
        });
    }
  }

  public hideControl() {
    if (this.hide && this.identifierControl !== document.activeElement) {
      this.showHiddenControl = true;
    }
  }

  public get entityIdentifierLabel(): string {
    switch (this.entityIdentifierQualifier) {
      case enumIdentifierQualifier.EIN:
        return 'LABELS.EIN_HINT';
      case enumIdentifierQualifier.SSN:
        return 'LABELS.SSN_HINT';
      case enumIdentifierQualifier.ANI:
        if (this.isHasAssignedCbpRequest) {
          return 'LABELS.INSERT_ASSIGNED_CBP_NUMBER';
        } else {
          return 'LABELS.ANI_HINT';
        }
      default:
        return 'LABELS.EIN_HINT';
    }
  }

  private get identifierControl(): HTMLInputElement {
    return document.getElementById(this.controlId) as any;
  }

  private get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }
}
